import { ImageName } from "@mcleod/images";
import { Alignment, Api, LogManager, ModelRow, Navigation, ObjectUtil, StringUtil, UrlUtil } from "@mcleod/core";
import {
    Button, ChangeEvent, Component, CrudDecorator, CrudDecoratorCloseEvent, DataSource, DataSourceExecutionEvent, DataSourceMode,
    Label, Layout, Snackbar, Table, TableRow, TableRowDisplayEvent, Textbox, Toast
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { ModelOrdersLookupAddStatus, StatusAddOrderDialog, ViewEdiStatus } from "@mcleod/datafusion";
import { IntercompanySlideout } from "@mcleod/datafusion/src/IntercompanySlideout";
import { AutogenLayoutShipmentStatusSlideout } from "./autogen/AutogenLayoutShipmentStatusSlideout";

const log = LogManager.getLogger("lme.dispatch.ShipmentStatusSlideout");

export class ShipmentStatusSlideout extends AutogenLayoutShipmentStatusSlideout {
    private _orderId: string;
    private _movementId: string;
    private _customerId: string;
    private _companyId: string;
    private _filterByMovement = false;
    private _rowsWithCustomDblClick = new WeakSet<TableRow>();
    private _saveButton = new Button({
        id: "",
        caption: "Save",
        backgroundColor: "primary",
        color: "primary.reverse",
        minWidth: 128,
        borderWidth: 0,
        rowBreak: false,
        enabled: false,
        tooltip: "Updates to the status record will be evaluated for corrections and resends."
    });
    private _pbLtl = false;
    private _hasIC = false;
    private _isSaving = false;
    private _promptedTrailerValues = new Map<ModelRow, string>();
    public inboundRows: ModelRow[];
    public outboundRows: ModelRow[];
    public inboundDataSource: DataSource = new DataSource();
    public outboundDataSource: DataSource = new DataSource();
    public inboundTenderRows: ModelRow[];
    public outboundTenderRows: ModelRow[];
    public inboundTenderDataSource: DataSource = new DataSource();
    public outboundTenderDataSource: DataSource = new DataSource();

    private _searchStatusURL = "lme/datafusion/StatusList?id="
    private _viewStatusURL = "lme/datafusion/ViewEdiStatus?mode=update&key=";

    override async onLoad(): Promise<void> {
        if (this.hasIC) {
            const icLayout = this.tabIntercompany?.findComponentById("layoutIntercompany") as IntercompanySlideout;
            icLayout.ediOrderId = this.orderId;
        } else {
            this.tabIntercompany.visible = false;
        }
    }

    private async searchByOrder(event: CrudDecoratorCloseEvent) {
        const orderId: string = event.dataHeader?.dataSource?.activeRow?.get("order_id");
        if (StringUtil.isEmptyString(orderId) === false) {
            log.debug("Searching for statuses with order ID %o", orderId);
            await this.sourceEdistatus.search({ order_id: orderId });
        }
    }

    delayCodeBeforeLookupModelSearch(event) {
        const delayCode = event.target as Textbox;
        const currRow = delayCode.boundRow?.data;
        const eventType = currRow["event_type"];
        let codeType = "DelayReason";

        if (eventType == "AP") {
            codeType = "AppointmentReason";
        }

        event.filter.stop_id = currRow["curr_stop_id"];
        event.filter.customer_id = currRow["customer_id"];
        event.filter.code_type = codeType;
        event.filter.partner_id = currRow["partner_id"];
        event.filter.version = currRow["version"];
    }

    sourceOrderEdistatusAfterExecution() {
        this.outboundRows = this.sourceEdistatus?.data?.filter((row) => row.data["direction"] === "O");
        this.outboundDataSource.data = this.outboundRows;
        this.tableOutbound.dataSource = this.outboundDataSource;
        this.tableOutbound.dataSource.mode = DataSourceMode.UPDATE;
        this.tableOutbound.dataSource.displayDataInBoundComponents();
        this.tabOutbound.visible = this.outboundRows?.length > 0;

        this.inboundRows = this.sourceEdistatus?.data?.filter((row) => row.data["direction"] === "I");
        this.inboundDataSource.data = this.inboundRows;
        this.tableInbound.dataSource = this.inboundDataSource;
        this.tableInbound.dataSource.displayDataInBoundComponents();
        this.tabInbound.visible = this.inboundRows?.length > 0;
    }

    sourceEdiOrderAfterExecution() {
        if (this.filterByMovement) {
            this.outboundTenderRows = this.sourceEdiOrder?.data?.filter((row) => row.data["direction"] === "O" && row.data["shipment_id"] === this.movementId);
            this.filterByMovement = false;
        } else {
            this.outboundTenderRows = this.sourceEdiOrder?.data?.filter((row) => row.data["direction"] === "O");
        }
        this.outboundTenderDataSource.data = this.outboundTenderRows;
        const tableOutboundTenders = this.tabOutboundTender?.findComponentById("tableOutboundTenders");
        tableOutboundTenders.dataSource.mode = DataSourceMode.UPDATE;
        tableOutboundTenders.dataSource = this.outboundTenderDataSource;
        tableOutboundTenders.dataSource.displayDataInBoundComponents();
        this.tabOutboundTender.visible = this.outboundTenderRows?.length > 0;

        this.inboundTenderRows = this.sourceEdiOrder?.data?.filter((row) => row.data["direction"] === "I");
        this.inboundTenderDataSource.data = this.inboundTenderRows;
        const tableInboundTenders = this.tabInboundTender?.findComponentById("tableInboundTenders") as Table;
        tableInboundTenders.dataSource.mode = DataSourceMode.UPDATE;
        tableInboundTenders.dataSource = this.inboundTenderDataSource;
        tableInboundTenders.dataSource.displayDataInBoundComponents();
        this.tabInboundTender.visible = this.inboundTenderRows?.length > 0;
        tableInboundTenders.dataSource.orderBy = [{ field: "create_date", sort: "desc" }];
        tableOutboundTenders.dataSource.orderBy = [{ field: "create_date", sort: "desc" }];
    }

    beforeExecute(event: DataSourceExecutionEvent) {
        if (ObjectUtil.isObject(event.filter) && Object.keys(event.filter).length == 0) {
            event.filter["empty_search"] = "*";
        }
    }

    async textboxOutDelayCodeOnChange(event) {
        const statusRow = event.target.row as TableRow;
        const row = statusRow.data as ModelRow;

        if (!row.hasChanged()) {
            log.debug("row has not changed");
            return;
        }

        const statusId = row.get("id");
        const delayCode = row.get("delay_code");

        await Api.search("lme/datafusion/validate-status", { status_id: statusId, in_delay_code: delayCode }).then((result) => {
            const errorCount = result.data[0]["error_count"];
            const labelErrorIcon = statusRow?.findComponentById("labelError") as Label;
            labelErrorIcon.text = errorCount;
            if (errorCount == "0 Errors") {
                labelErrorIcon.imageName = ImageName.circleCheckEmpty;
                labelErrorIcon.color = "success";
            }
            else {
                labelErrorIcon.imageName = ImageName.circleErrorSolid;
                labelErrorIcon.color = "error";
            }
        });
    }

    async textboxOutTrailerOnChange(event: ChangeEvent) {
        const oldValue = event.oldValue;
        const newValue = event.newValue;

        if (!this._isSaving && oldValue !== newValue && event.userInitiatedChange) {
            this.saveButton.enabled = true;
        }
    }

    private async promptApplyTrailerToAll(changedRow: ModelRow): Promise<void> {
        // Only prompt if this row was actually changed
        if (!changedRow.hasChanged()) {
            return;
        }

        const newTrailerValue = changedRow.get("carrier_trailer");
        if (StringUtil.isEmptyString(newTrailerValue)) {
            return;
        }

        // Don't prompt again if we already prompted for this value on this row
        if (this._promptedTrailerValues.get(changedRow) === newTrailerValue) {
            return;
        }

        // Find other outbound rows that could receive this value
        const otherRows = this.outboundDataSource.data?.filter(row =>
            row !== changedRow &&
            row.get("brokerage") === "Y" &&
            row.get("carrier_trailer") !== newTrailerValue &&
            row.get("batch") === 0
        ) ?? [];

        if (otherRows.length === 0) {
            return;
        }

        // Track that we've prompted for this value to avoid repeated prompts
        this._promptedTrailerValues.set(changedRow, newTrailerValue);

        const plural = otherRows.length > 1 ? "es" : "";
        const confirmed = await CommonDialogs.showYesNo(
            `Would you like to update the carrier trailer to "${newTrailerValue}" on the remaining ${otherRows.length} status${plural} (with a batch of 0)?`,
            "Update All Statuses"
        );

        if (confirmed) {
            for (const row of otherRows) {
                row.set("carrier_trailer", newTrailerValue);
            }
            this.tableOutbound.dataSource.displayDataInBoundComponents();
        }
    }

    tableOutboundOnRowDisplay(event: TableRowDisplayEvent) {
        const currRow = event.target as TableRow;
        const currModelRow = currRow.data as ModelRow;
        const errorCount = currModelRow.get("error_count");
        const errorIcon = currRow?.findComponentById("labelError") as Label;
        const eventType = currModelRow?.get("event_type");
        const partnerName = currRow?.findComponentById("labelPartnerOut") as Label;
        this.setTooltipContent(currModelRow, partnerName);

        const labelStopOut = currRow?.findComponentById("labelStopOut") as Label;
        const labelStatusOut = currRow?.findComponentById("labelStatusOut") as Label;
        const labelStatusDateOut = currRow?.findComponentById("labelStatusDateOut") as Label;
        this.setTooltipContent(currModelRow, labelStopOut);
        this.setTooltipContent(currModelRow, labelStatusOut);
        this.setTooltipContent(currModelRow, labelStatusDateOut);


        let codeType = "DelayReason";

        const labelTransmitted = currRow?.findComponentById("labelTransmissionStatus") as Label;
        if (labelTransmitted.text !== "Not Sent") {
            this.setTooltipContent(currModelRow, labelTransmitted);
        }

        if (eventType == "AP") {
            codeType = "AppointmentReason";
        }

        if (errorCount == "0 Errors") {
            errorIcon.imageName = ImageName.circleCheckEmpty;
            errorIcon.color = "success";
        }
        else {
            errorIcon.imageName = ImageName.circleErrorSolid;
            errorIcon.color = "error";
            this.setTooltipContent(currModelRow, errorIcon);
        }

        const statusRow = event.target as TableRow;
        const isBrokerage = statusRow?.data?.get("brokerage") === "Y"? true : false;
        const textBoxCarrier = currRow?.findComponentById("textboxCarrierTrailer") as Textbox;
        const labelTrailerId = currRow?.findComponentById("labelTrailerId");
        if (isBrokerage) {
            textBoxCarrier.visible = true;
            labelTrailerId.visible = false;
            textBoxCarrier.addBlurListener(() => this.promptApplyTrailerToAll(currModelRow));
        }
        else {
            labelTrailerId.visible = !StringUtil.isEmptyString(currModelRow.get("trailer_id")) ? true : false;
            textBoxCarrier.visible = false;
        }

        const delayCodeTxtBox = currRow?.findComponentById("textboxOutDelayCode") as Textbox;
        delayCodeTxtBox.quickInfoOverrideSearchMethod = async (layout, keyValue) => {
            await layout.mainDataSource.search({ standard_code: keyValue });
        };
        delayCodeTxtBox?.addLookupModelSelectionListener(() => {
            if (!this._isSaving) {
                this.saveButton.enabled = true;
            }
        });

        this.addRowDoubleClickListener(event);
    }

    checkInMapErrors(event: TableRowDisplayEvent) {
        const currRow = event.target as TableRow;
        const currModelRow = currRow.data;
        const errorCount = currModelRow.get("error_count");
        const errorIcon = currRow?.findComponentById("labelInboundError") as Label;

        if (errorCount == "0 Errors") {
            errorIcon.imageName = ImageName.circleCheckEmpty;
            errorIcon.color = "success";
        }
        else {
            errorIcon.imageName = ImageName.circleErrorSolid;
            errorIcon.color = "error";
            this.setTooltipContent(currModelRow, errorIcon);
        }
        const partnerName = currRow?.findComponentById("labelInboundPartner") as Label;
        this.setTooltipContent(currModelRow, partnerName);

        const labelInboundStop = currRow?.findComponentById("labelInboundStop") as Label;
        const labelInboundStatusDesc = currRow?.findComponentById("labelInboundStatusDesc") as Label;
        const labelInboundStatusDate = currRow?.findComponentById("labelInboundStatusDate") as Label;
        this.setTooltipContent(currModelRow, labelInboundStop);
        this.setTooltipContent(currModelRow, labelInboundStatusDesc);
        this.setTooltipContent(currModelRow, labelInboundStatusDate);

        const delayCode = currRow?.findComponentById("labelInboundDelay") as Label;
        this.setTooltipContent(currModelRow, delayCode);

        this.addRowDoubleClickListener(event);
    }

    async getDelayCode(currDelay: string, version: string): Promise<ModelRow> {
        return await Api.search("lme/datafusion/delay-code", { delay_code: currDelay, version: version });
    }


    private setTooltipContent(modelRow: ModelRow, component: Component) {
        if (StringUtil.isEmptyString(component?.id)) return;
        switch (component?.id) {
            case "labelError":
            case "labelInboundError":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/StatusErrorQuickInfo", null,
                    {
                        "parent_id": modelRow.get("id"), "parent_tablename": "edistatus"
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.TOP };
                return;


            case "labelInboundDelay":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/SrcQuickInfo", null,
                    {
                        "standard_code": modelRow.get("delay_code")
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.TOP };
                return;
            case "labelTransmissionStatus":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/TransmissionStatusQuickInfo", null,
                    {
                        "direction": modelRow.get("direction"),
                        "batch": modelRow.get("batch"),
                        "func_receiver_id": modelRow.get("partner_id"),
                        "receiver_id": modelRow.get("alt_partner_id"),
                        "func_version": modelRow.get("version"),
                        "transaction_type": "S"
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.TOP };
                return;
            case "labelPartnerOut":
            case "labelInboundPartner":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/EdiProfileStatusQuickInfo", null,
                    {
                        "direction": modelRow.get("direction"),
                        "partner_id": modelRow.get("partner_id"),
                        "alt_partner_id": modelRow.get("alt_partner_id"),
                        "version": modelRow.get("version"),
                        "batch": modelRow.get("batch")
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.TOP };
                return;
            case "labelStopOut":
            case "labelStatusOut":
            case "labelStatusDateOut":
            case "labelInboundStop":
            case "labelInboundStatusDesc":
            case "labelInboundStatusDate":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/StatusQuickInfo", null,
                    {
                        "id": modelRow.get("id")
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.TOP };
                return;
        }
    }

    // This method and its custom CrudDecorator is only necessary because the normal double-click to detail the table
    // row doesn't work.  The reason it doesn't work is that the table's row control mode prevents the table's selected
    // row value from being set when a row is single-clicked.
    private addRowDoubleClickListener(event: TableRowDisplayEvent) {
        const currRow = event.target as TableRow;
        const currModelRow = currRow.data;
        const table = event.getTable();

        if (!this._rowsWithCustomDblClick.has(currRow) && table.detailLayout != null) {
            this._rowsWithCustomDblClick.add(currRow);
            currRow.addDblClickListener(async () => {
                const urlParam = { mode: "update", key: currModelRow.get("id") };
                const url = "/" + table.detailLayout + UrlUtil.buildQueryString(urlParam);
                const layout = Layout.getLayout(table.detailLayout) as ViewEdiStatus;

                new CrudDecorator({
                    layout: layout,
                    mode: DataSourceMode.UPDATE,
                    pseudoNavigateUrl: url,
                    headerProps: {
                        showClose: true,
                        showSaveAndClose: true,
                        // This would use the afterCrudCloseListener in Table if these tables could use the
                        // stock CrudDecorator
                        doAfterClose: async (event: CrudDecoratorCloseEvent) => {
                            if (event.isCancel() !== true) {
                                // Wait for any post-save validation to complete before refreshing
                                await layout.postSaveValidationComplete;
                                await this.searchByOrder(event);
                            }
                        }
                    },
                    doAfterSlideOut: () => {
                        if (layout.saveDataToOrder) {
                            Toast.showSuccessToast("Changes saved to the order " + currModelRow.get("order_id") + ".", null, null, {millisUntilDismissal: 5000});
                        }
                    },
                    key: currModelRow.get("id")
                }).slideIn({ speed: 200 }, null, null, { paddingAll: 0 });
            });
        }
    }

    buttonAdditionalInfoOnClickOut() {
        const orderId = this.sourceEdistatus?.data[0]?.get("order_id") as string;
        Navigation.navigateTo(this.searchStatusURL + orderId, { newTab: true });
    }

    /** This is an event handler for the afterExecution event of sourceEdiMovementCarrier.  */
    sourceEdiMovementCarrierAfterExecution(event: DataSourceExecutionEvent) {
        const isLoadTenderCarrier = this.sourceEdiMovementCarrier?.activeRow?.data["is_loadtender_carrier"];
        const isStatusCarrier = this.sourceEdiMovementCarrier?.activeRow?.data["is_status_carrier"];
        this.tabOutboundTender.visible = isLoadTenderCarrier;
        this.tabInbound.visible = this._pbLtl || isStatusCarrier || this.inboundRows?.length > 0;
    }

    /** This is an event handler for the afterExecution event of sourceEdiCustomer.  */
    sourceEdiCustomerAfterExecution(event: DataSourceExecutionEvent) {
        const ediCustomer = this.sourceEdiCustomer?.activeRow?.data["customer_id"];
        this.tabOutbound.visible = !StringUtil.isEmptyString(ediCustomer) || this.outboundRows?.length > 0;
    }

    sourceEdiMovementOrderIcAfterExecution(event: DataSourceExecutionEvent) {
        this.hasIC = this.sourceEdiMovementOrderIc?.data?.length > 0;
    }

    /**
     * Saves all changed rows in the outbound data source.
     * Disables the save button after a successful save.
     * After all status posts, updates the movement record once with the carrier trailer.
     * @returns Promise that resolves when all saves complete
     */
    async saveChanges(): Promise<void> {
        this._isSaving = true;
        this._saveButton.busy = true;
        try {
            const changedRows = this.outboundDataSource.data?.filter(row => row.hasChanged()) ?? [];

            // Check if any row has carrier_trailer in its changed data
            const rowWithTrailerChange = changedRows.find(row => {
                const changedData = row.getChangedData();
                return changedData && "carrier_trailer" in changedData;
            });
            const carrierTrailer = rowWithTrailerChange?.get("carrier_trailer");

            // Set from_status_slideout flag on each row to skip movement update in ModelEdiStatus
            changedRows.forEach(row => row.set("from_status_slideout", true));

            await Promise.all(changedRows.map(row => row.post()));

            // Update the movement record once after all status posts complete
            if (this.movementId && carrierTrailer) {
                await Api.post("lme/datafusion/update-move-from-status", {
                    movement_id: this.movementId,
                    carrier_trailer: carrierTrailer
                });
            }

            // Wait multiple frames to ensure all async UI updates complete while _isSaving is true
            await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
        } finally {
            this._saveButton.busy = false;
            // Only disable if no rows have pending changes (could have new changes during save)
            const hasUnsavedChanges = this.outboundDataSource.data?.some(row => row.hasChanged()) ?? false;
            this._saveButton.enabled = hasUnsavedChanges;
            this._promptedTrailerValues.clear();
            this._isSaving = false;
        }
    }

    set orderId(value: string) {
        this._orderId = value;
    }

    get orderId(): string {
        return this._orderId;
    }

    public get movementId() {
        return this._movementId;
    }

    public set movementId(value) {
        this._movementId = value;
    }

    public get customerId() {
        return this._customerId;
    }

    public set customerId(value) {
        this._customerId = value;
    }

    public get filterByMovement(): boolean {
        return this._filterByMovement;
    }

    public set filterByMovement(value: boolean) {
        this._filterByMovement = value;
    }

    public get saveButton() {
        return this._saveButton;
    }

    public set saveButton(value: Button) {
        this._saveButton = value;
    }

    get searchStatusURL() {
        return this._searchStatusURL;
    }

    get viewStatusURL() {
        return this._viewStatusURL;
    }

    get pbLtl() {
        return this._pbLtl;
    }

    public set pbLtl(value: boolean) {
        this._pbLtl = value;
    }

    public get companyId() {
        return this._companyId;
    }

    public set companyId(value) {
        this._companyId = value;
    }

    public get hasIC() {
        return this._hasIC;
    }

    public set hasIC(value) {
        this._hasIC = value;
        this.tabIntercompany.visible = value;
    }

    async addButtonOnClick() {
        this.buttonAddNewStatus.busy = true;
        let orderId = this.orderId;
        let partnerId = "";
        let altPartnerId = "";
        let version = "";
        const numberOfValues = await new ModelOrdersLookupAddStatus().search({ "orders.id": this.orderId });
        if (numberOfValues?.actualRowCount === 1) {
            partnerId = numberOfValues?.modelRows[0]?.get("edi_customer.partner_id");
            altPartnerId = numberOfValues?.modelRows[0]?.get("edi_customer.alt_partner_id");
            version = numberOfValues?.modelRows[0]?.get("edi_customer.version");
        } else {
            const dialog = new StatusAddOrderDialog({ okVisible: false, title: "Add New Status" });
            dialog.orderId = orderId;
            const response = await dialog.show();
            orderId = response.order_id;
            partnerId = response.partner_id;
            altPartnerId = response.alt_partner_id;
            version = response.version;
        }

        if (!StringUtil.isEmptyString(orderId)
            && !StringUtil.isEmptyString(partnerId)
            && !StringUtil.isEmptyString(altPartnerId)
            && !StringUtil.isEmptyString(version)) {
            Api.post("lme/datafusion/add-order-to-shipment", {
                order_id: orderId,
                partner_id: partnerId,
                alt_partner_id: altPartnerId,
                version: version,
                direction: "O"
            }).then(async apiResponse => {
                const orderId = apiResponse?.data[0]?.order_id;
                if (!StringUtil.isEmptyString(orderId)) {
                    const statusLayout = Layout.getLayout(this.tableOutbound.detailLayout) as ViewEdiStatus;
                    const urlParam = { mode: "add", key: orderId };
                    const url = "/" + this.tableOutbound.detailLayout + UrlUtil.buildQueryString(urlParam);
                    const statusPanel = new CrudDecorator({
                        layout: statusLayout,
                        mode: DataSourceMode.ADD,
                        pseudoNavigateUrl: url,
                        headerProps: {
                            showClose: true,
                            showSaveAndClose: true,
                        },
                        key: orderId,
                        providedApiData: apiResponse.data[0],
                        doAfterSlideOut: async () => {
                            if (this.sourceEdistatus.lastSearch) {
                                await this.sourceEdistatus.search(this.sourceEdistatus.lastSearch);
                            }
                        },
                    }) as any;
                    await statusPanel.slideIn();

                } else {
                    Snackbar.showWarningSnackbar("Invalid input for Order Id.");
                }
                this.buttonAddNewStatus.busy = false;
            })
        } else {
            this.buttonAddNewStatus.busy = false;
        }
    }
}
